// Global Cmd+K / Ctrl+K search — jump to any page, company, or opportunity from
// anywhere in the app. Mounted once at the Shell root, opened by the header's
// search pill or the shortcut; closed by Escape or a backdrop click, mirroring
// ConfirmDialog's overlay + keydown convention.
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { CompanyDto, OpportunityDto } from "@arcline/shared";
import { api } from "../lib/api";
import { NAV } from "../lib/nav";
import { c, font } from "../lib/theme";

interface Result { key: string; group: "Page" | "Company" | "Opportunity"; label: string; sub?: string; glyph: string; to: string }

const PAGES: Result[] = NAV.flatMap((grp) => grp.items.map((it) => ({ key: `page-${it.to}`, group: "Page" as const, label: it.label, sub: grp.group, glyph: it.glyph, to: it.to })));

function rank(items: Result[], needle: string, limit: number): Result[] {
  const n = needle.toLowerCase();
  return items
    .filter((r) => r.label.toLowerCase().includes(n) || (r.sub ?? "").toLowerCase().includes(n))
    .sort((a, b) => Number(!a.label.toLowerCase().startsWith(n)) - Number(!b.label.toLowerCase().startsWith(n)))
    .slice(0, limit);
}

export function CommandPalette() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [active, setActive] = useState(0);
  const [companies, setCompanies] = useState<CompanyDto[] | null>(null);
  const [opportunities, setOpportunities] = useState<OpportunityDto[] | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const openPalette = (): void => {
    setOpen(true); setQ(""); setActive(0);
    if (!companies) api.companies().then(setCompanies).catch(() => setCompanies([]));
    if (!opportunities) api.opportunities().then(setOpportunities).catch(() => setOpportunities([]));
  };

  // Global shortcut — always listening, regardless of open state.
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") { e.preventDefault(); openPalette(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companies, opportunities]);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const results = useMemo<Result[]>(() => {
    const needle = q.trim();
    if (!needle) return PAGES.slice(0, 8);
    const companyResults = rank((companies ?? []).map((co) => ({ key: `co-${co.id}`, group: "Company" as const, label: co.name, sub: [co.sector, co.country].filter(Boolean).join(" · "), glyph: "▣", to: `/companies?company=${co.id}` })), needle, 6);
    const oppResults = rank((opportunities ?? []).map((o) => ({ key: `opp-${o.id}`, group: "Opportunity" as const, label: o.title, sub: o.agency ?? undefined, glyph: "◆", to: `/opportunities/${o.id}` })), needle, 6);
    const pageResults = rank(PAGES, needle, 6);
    return [...pageResults, ...companyResults, ...oppResults];
  }, [q, companies, opportunities]);

  // Esc closes; arrows move selection; Enter navigates — only while open.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === "Escape") { e.preventDefault(); setOpen(false); }
      else if (e.key === "ArrowDown") { e.preventDefault(); setActive((i) => Math.min(i + 1, results.length - 1)); }
      else if (e.key === "ArrowUp") { e.preventDefault(); setActive((i) => Math.max(i - 1, 0)); }
      else if (e.key === "Enter") { e.preventDefault(); const r = results[active]; if (r) { navigate(r.to); setOpen(false); } }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, results, active, navigate]);

  useEffect(() => { setActive(0); }, [q]);

  return (
    <>
      <span onClick={openPalette} title="Search (Ctrl/Cmd+K)" role="button" aria-label="Search" className="arc-hdr-btn"
        style={{ display: "flex", alignItems: "center", gap: 6, font: `600 12.5px ${font.sans}`, letterSpacing: "0.04em", color: c.muted2, background: c.soft.tileHi, border: "none", borderRadius: 8, padding: "5px 10px", cursor: "pointer" }}>
        <span className="arc-desktop-only">⌘K</span><svg className="arc-phone-only" aria-hidden width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round"><circle cx="10.5" cy="10.5" r="6.5" /><path d="M15.5 15.5 21 21" /></svg><span className="arc-label">Search</span>
      </span>
      {open && (
        <div onClick={() => setOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(3,5,11,0.72)", display: "grid", placeItems: "center", zIndex: 200, paddingTop: "10vh" }}>
          <div role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}
            style={{ width: 520, maxWidth: "90vw", maxHeight: "70vh", display: "flex", flexDirection: "column", background: c.panel, border: `1px solid ${c.line2}`, borderRadius: 8, boxShadow: "0 24px 64px rgba(0,0,0,0.55)", overflow: "hidden" }}>
            <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search pages, companies, opportunities…"
              style={{ background: "transparent", border: "none", borderBottom: `1px solid ${c.line}`, padding: "16px 18px", color: c.textHi, fontSize: 16, outline: "none" }} />
            <div style={{ flex: 1, overflowY: "auto", padding: "6px 0" }}>
              {results.length === 0 && <div style={{ padding: "16px 18px", fontSize: 14, color: c.muted, fontStyle: "italic" }}>No matches for “{q.trim()}”.</div>}
              {results.map((r, i) => (
                <div key={r.key} onClick={() => { navigate(r.to); setOpen(false); }} onMouseEnter={() => setActive(i)}
                  style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 18px", cursor: "pointer", background: i === active ? c.navItem : "transparent" }}>
                  <span style={{ width: 16, fontSize: 14, color: c.blueHi, flex: "none" }}>{r.glyph}</span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 14.5, color: c.textHi, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.label}</div>
                    {r.sub && <div style={{ font: `500 13px ${font.sans}`, color: c.muted, marginTop: 1 }}>{r.sub}</div>}
                  </div>
                  <span style={{ font: `500 11px ${font.mono}`, letterSpacing: "0.08em", color: c.muted, flex: "none" }}>{r.group.toUpperCase()}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
